import { useState } from "react";
import { useAxios } from "../../../utils/useAxios";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} ClassroomPivot
 * @property {number} lesson_id
 * @property {number} classroom_id
 * @property {string} status
 * @property {string} assigned_at
 * @property {string} due_date
 */

/**
 * @typedef {Object} ClassroomType
 * @property {number} id
 * @property {string} name
 * @property {string} classroom_code
 * @property {string} status
 * @property {ClassroomPivot} pivot
 * @property {string} start_date
 * @property {string} end_date
 */

/**
 * @typedef {Object} QuestPivot
 * @property {number} lesson_id
 * @property {number} quest_id
 * @property {number} order_index
 */

/**
 * @typedef {Object} QuestType
 * @property {number} id
 * @property {string} title
 * @property {string} description
 * @property {string} thumbnail
 * @property {string} status
 * @property {QuestPivot} pivot
 */

/**
 * @typedef {Object} LessonDetailType
 * @property {number} id
 * @property {string} tenant_id
 * @property {string} title
 * @property {string} description
 * @property {string} lesson_code
 * @property {string} created_at
 * @property {number} created_by
 * @property {ClassroomType[]} classrooms
 * @property {QuestType[]} quests
 */

/**
 * @typedef {Object} GetLessonDetailParams
 * @property {number} lessonId - Lesson ID (required)
 */

/**
 * Custom hook to fetch the lesson detail by lesson id
 */
export const useGetLessonDetail = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lessonDetail, setLessonDetail] = useState(
    /** @type {LessonDetailType | null | undefined} */ (undefined),
  );
  const { axiosInstance } = useAxios();

  /**
   * Fetch lesson details by ID
   * @async
   * @param {GetLessonDetailParams} params
   * @returns {Promise<void>}
   */
  const getLessonDetail = async ({ lessonId } = {}) => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosInstance.get(
        `${TENANT_DOMAIN}/lesson/edit/${lessonId}`,
      );

      setLessonDetail(response?.data?.data || null);
    } catch (err) {
      setLessonDetail(null);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    lessonDetail,
    loading,
    error,
    getLessonDetail,
    setLessonDetail,
  };
};
